import { useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { tradingSlice } from '!views/index';
import { TrlFooterLink, TrlSectionTitle } from '»components/atoms';
import { menusSlice } from '»components/Menus';
import { TrlGrid } from '»components/molecules';
import { useGridRemainingHeight } from '»utils/customHooks';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { Container } from './TrlGridWithTitle.styles';

export const TrlGridWithTitle = ({
  componentId,
  componentWidth,
  componentMinHeight,
  componentMaxHeight,
  title,
  gridColumns,
  gridData,
  gridDataItemKey,
  footerLabel,
  route,
  activeNavigationMenuItem,
  activeMenuItem,
  componentOrder,
  selectedItem,
  selectedItemChange,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const containerRef = useRef(null);

  const getRemainingHeight = useCallback(
    ({ gridContent, rowsHeight }) => {
      const gridContentTop = gridContent?.getBoundingClientRect().top;
      const footerTop = footerLabel
        ? containerRef.current?.children[2]?.getBoundingClientRect().top
        : undefined;
      const containerBottom =
        containerRef.current?.getBoundingClientRect().bottom;

      const availableHeight =
        typeof gridContentTop === 'number'
          ? typeof footerTop === 'number'
            ? footerTop - gridContentTop
            : containerBottom - gridContentTop
          : gridContent?.clientHeight || 0;

      return availableHeight - rowsHeight - (gridData.length === 0 ? 4 : 0) - 4; // first 4 is borderBottom: 4px solid #F2F4F9 !important;
    },
    [footerLabel, gridData.length],
  );

  const { remainingGridHeight, hasHorizontalScroll, hasVerticalScroll } =
    useGridRemainingHeight({
      containerRef: containerRef,
      gridDataLength: gridData.length,
      customGridRoot: document.querySelector(`${componentId} > .k-grid`),
      getRemainingHeight,
    });

  const navigateToPage = () => {
    if (activeNavigationMenuItem === 'TRADING')
      dispatch(tradingSlice.actions.setCurrentTab(0));
    navigate(route);
    dispatch(
      menusSlice.actions.setActiveNavigationMenuItem({
        activeNavigationMenuItem,
      }),
    );
    dispatch(
      menusSlice.actions.setActiveMenuItem({
        activeMenuItem,
      }),
    );
  };

  return (
    <Container
      id={componentId}
      ref={containerRef}
      width={componentWidth}
      minHeight={componentMinHeight}
      maxHeight={componentMaxHeight}
      componentOrder={componentOrder}
    >
      <TrlSectionTitle
        title={TrlFormattedMessagePlain({ id: title })}
        hasPadding
        hasBackground
        otherBackgroundColor={theme.palette.colors.primaryBackground}
      />
      <TrlGrid
        columns={gridColumns}
        data={gridData}
        noBorders
        hasTransparentBackground
        noMarginTop
        dataItemKey={gridDataItemKey}
        remainingGridHeight={remainingGridHeight}
        otherGridColor={theme.palette.colors.primaryBackground}
        otherScrollColor={theme.palette.colors.primaryBackground}
        otherFirefoxSecScrollColor={theme.palette.colors.infoBackground}
        hasHorizontalScroll={hasHorizontalScroll}
        hasVerticalScroll={hasVerticalScroll}
        selectedItem={selectedItem}
        selectedItemChange={selectedItemChange}
      />
      {footerLabel && (
        <TrlFooterLink
          navigateTo={navigateToPage}
          label={footerLabel}
          atTheBottom
          backgroundColor={theme.palette.colors.primaryBackground}
        />
      )}
    </Container>
  );
};

TrlGridWithTitle.propTypes = {
  componentId: PropTypes.string,
  componentWidth: PropTypes.string,
  componentMinHeight: PropTypes.string.isRequired,
  componentMaxHeight: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  gridColumns: PropTypes.array.isRequired,
  gridData: PropTypes.array.isRequired,
  gridDataItemKey: PropTypes.string.isRequired,
  footerLabel: PropTypes.string,
  route: PropTypes.string,
  activeNavigationMenuItem: PropTypes.string,
  activeMenuItem: PropTypes.string,
  componentOrder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedItem: PropTypes.object,
  selectedItemChange: PropTypes.func,
};
